// src/features/patients/prescriptions/utils/prescriptionPdf.js
//
// Renderiza o receituário montado por prescriptionDocument.js num PDF A4 e
// dispara o download.
//
// Enquadramento: segue exatamente as mesmas regras do printPrescription.js —
// folha A4, timbre como background, conteúdo dentro do `content_area` salvo —
// para que o que se imprime e o que se baixa sejam o mesmo documento. Em vez de
// abrir uma janela e chamar window.print(), cada folha é rasterizada com
// html2canvas e empilhada num jsPDF.
//
// Paginação: o conteúdo é quebrado em blocos de nível superior e cada folha
// recebe seu próprio timbre e sua própria faixa de rodapé (nº da prescrição,
// paciente e "página N de M"). Nada é esticado quando passa de uma página.

import jsPDF from "jspdf";
import html2canvas from "html2canvas";

import { escapeHtml } from "./prescriptionSanitize";
import { buildPrescriptionDocument } from "./prescriptionDocument";
import { comNonce } from "@/utils/cspNonce";

const MM_TO_PX = 96 / 25.4;
const A4_W_MM = 210;
const A4_H_MM = 297;
const A4_W_PX = Math.round(A4_W_MM * MM_TO_PX); // ≈ 794
const A4_H_PX = Math.round(A4_H_MM * MM_TO_PX); // ≈ 1123

const mm = (v) => v * MM_TO_PX;

// Faixa reservada no pé de cada folha para a numeração de páginas.
const PAGE_FOOTER_PX = 26;

const ACCENT = {
  medication: "#3E8E73",
  exam: "#2E7D32",
  supplement: "#EF6C00",
  mixed: "#3E8E73",
};

// ────────────────────────────────────────────────────────────────────────────
// Layout da folha
// ────────────────────────────────────────────────────────────────────────────

function normalizeArea(area) {
  if (!area || typeof area !== "object") return null;
  const n = (v) => (Number.isFinite(+v) ? +v : null);
  const top = n(area.top);
  const left = n(area.left);
  const width = n(area.width);
  const height = n(area.height);
  if (top == null || left == null || width == null || height == null) return null;
  if (width <= 0 || height <= 0) return null;
  return { top, left, width, height };
}

/**
 * Converte a configuração de timbre num retângulo em pixels dentro da folha A4.
 * Espelha os paddings do printPrescription.js para que impressão e download
 * produzam o mesmo enquadramento.
 */
function resolveContentRect(hasLetterhead, contentArea) {
  const area = hasLetterhead ? normalizeArea(contentArea) : null;

  if (area) {
    return {
      top: (area.top / 100) * A4_H_PX,
      left: (area.left / 100) * A4_W_PX,
      width: (area.width / 100) * A4_W_PX,
      height: (area.height / 100) * A4_H_PX,
    };
  }

  if (hasLetterhead) {
    // padding: 55mm 18mm 40mm 18mm
    return {
      top: mm(55),
      left: mm(18),
      width: A4_W_PX - mm(36),
      height: A4_H_PX - mm(95),
    };
  }

  // padding: 18mm 15mm
  return {
    top: mm(18),
    left: mm(15),
    width: A4_W_PX - mm(30),
    height: A4_H_PX - mm(36),
  };
}

const STYLE_ID = "sv-prescription-pdf-styles";

function ensureStyles() {
  if (document.getElementById(STYLE_ID)) return;

  // SALVIA-746: com nonce, ou a CSP descarta a folha e a receita sai crua.
  const style = comNonce(document.createElement("style"));
  style.id = STYLE_ID;
  style.textContent = `
.sv-pdf-stage {
  position: fixed;
  left: -20000px;
  top: 0;
  width: ${A4_W_PX}px;
  z-index: -1;
  pointer-events: none;
}
.sv-pdf-sheet {
  width: ${A4_W_PX}px;
  height: ${A4_H_PX}px;
  position: relative;
  box-sizing: border-box;
  overflow: hidden;
  background-color: #ffffff;
  background-size: 100% 100%;
  background-position: top left;
  background-repeat: no-repeat;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
  font-size: 12.5px;
  line-height: 1.55;
  color: #263238;
}
.sv-pdf-area {
  position: absolute;
  box-sizing: border-box;
  overflow: hidden;
}

/* ── Emitente (só sem timbre) ── */
.doc-issuer {
  text-align: center;
  padding-bottom: 10px;
  margin-bottom: 14px;
  border-bottom: 2px solid var(--sv-accent);
}
.doc-issuer-name {
  font-size: 17px;
  font-weight: 700;
  letter-spacing: -0.2px;
  color: var(--sv-accent);
}
.doc-issuer-line { font-size: 11px; color: #546E7A; }

/* ── Título ── */
.doc-title-block { text-align: center; margin: 0 0 16px; }
.doc-title {
  font-size: 15px;
  font-weight: 700;
  letter-spacing: 1.6px;
  color: #263238;
}
.doc-title-meta { font-size: 11px; color: #607D8B; margin-top: 2px; }

/* ── Seções (achatadas: título e filhos são irmãos, para poder paginar) ── */
.doc-section-title {
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.9px;
  color: var(--sv-accent);
  border-bottom: 1px solid #CFD8DC;
  padding-bottom: 3px;
  margin: 14px 0 7px;
}
.sv-pdf-area > .doc-section-title:first-child { margin-top: 0; }

/* ── Campos de identificação ── */
.doc-fields { display: block; }
.doc-field { margin: 2px 0; font-size: 12px; }
.doc-field-label {
  font-weight: 700;
  color: #455A64;
  margin-right: 5px;
}
.doc-field-label::after { content: ":"; }
.doc-field-value { color: #263238; }

/* ── CID ── */
.doc-cid { font-size: 12px; margin: 2px 0; }
.doc-cid-code {
  font-weight: 700;
  color: #0E7490;
  margin-right: 4px;
}

/* ── Itens prescritos ── */
.doc-item {
  padding: 8px 11px;
  margin: 0 0 7px;
  border-left: 3px solid var(--sv-accent);
  background-color: #F5F7F6;
  border-radius: 3px;
}
.doc-item-head {
  display: flex;
  align-items: baseline;
  font-size: 13px;
  font-weight: 700;
  color: #1B2A32;
}
.doc-item-index { margin-right: 5px; }
.doc-item-name { flex: 1; }
.doc-item-qty {
  margin-left: 10px;
  white-space: nowrap;
  font-weight: 700;
}
.doc-item-detail {
  font-size: 11.5px;
  color: #37474F;
  margin-top: 2px;
  padding-left: 15px;
}
.doc-item-detail-label { font-weight: 700; color: #546E7A; }

/* ── Texto livre do editor (entra sem wrapper, para poder paginar) ── */
.sv-pdf-area p { margin: 3px 0; font-size: 12px; }
.sv-pdf-area ul, .sv-pdf-area ol { padding-left: 24px; margin: 5px 0; font-size: 12px; }
.sv-pdf-area li { margin: 2px 0; }
.sv-pdf-area strong, .sv-pdf-area b { font-weight: 700; }
.sv-pdf-area em, .sv-pdf-area i { font-style: italic; }
.sv-pdf-area u { text-decoration: underline; }

/* ── Assinatura ── */
.doc-signature { margin-top: 22px; text-align: center; }
.doc-signature-place {
  font-size: 12px;
  color: #37474F;
  margin-bottom: 26px;
  text-align: right;
}
.doc-signature-line {
  width: 260px;
  margin: 0 auto 5px;
  border-top: 1px solid #455A64;
}
.doc-signature-name { font-size: 12.5px; font-weight: 700; color: #1B2A32; }
.doc-signature-reg { font-size: 11.5px; color: #455A64; }
.doc-signature-hint {
  font-size: 9.5px;
  color: #90A4AE;
  text-transform: uppercase;
  letter-spacing: 0.6px;
  margin-top: 2px;
}

/* ── Rodapé de página ── */
.sv-pdf-pagefoot {
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  height: ${PAGE_FOOTER_PX}px;
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  font-size: 9.5px;
  color: #90A4AE;
  border-top: 1px solid #ECEFF1;
  padding-top: 4px;
}
`;
  document.head.appendChild(style);
}

/**
 * Filhos diretos que participam da paginação. Texto solto é embrulhado num
 * elemento para poder ser medido e movido de folha.
 */
function collectBlocks(parent) {
  return Array.from(parent.childNodes)
    .map((node) => {
      if (node.nodeType === 1) return node;
      if (node.nodeType === 3 && node.textContent.trim()) {
        const wrapper = document.createElement("div");
        wrapper.textContent = node.textContent;
        node.parentNode.replaceChild(wrapper, node);
        return wrapper;
      }
      return null;
    })
    .filter(Boolean);
}

/** Altura ocupada por um nó, incluindo as margens verticais colapsáveis. */
function outerHeight(el) {
  const rect = el.getBoundingClientRect();
  const cs = window.getComputedStyle(el);
  return rect.height + parseFloat(cs.marginTop || 0) + parseFloat(cs.marginBottom || 0);
}

const isSectionTitle = (el) => el.classList?.contains("doc-section-title");

/**
 * Distribui os blocos do conteúdo entre as folhas, respeitando a altura útil.
 * Retorna um array de arrays de nós (um por folha).
 *
 * Duas regras vêm de como um receituário precisa se comportar em papel:
 *   - título de seção não fica órfão no pé da folha (quebra antes dele);
 *   - seção que atravessa a folha repete o título com "(cont.)", senão a
 *     segunda página apareceria com uma lista de itens sem dizer do que é.
 */
function paginateBlocks(blocks, usableHeight, signatureHeight) {
  const pages = [];
  let current = [];
  let used = 0;
  let sectionTitle = null;
  let sectionTitleHeight = 0;

  const breakPage = (continuesSection) => {
    pages.push(current);
    current = [];
    used = 0;

    if (continuesSection && sectionTitle) {
      const continuation = sectionTitle.cloneNode(true);
      continuation.textContent = `${sectionTitle.textContent} (cont.)`;
      current.push(continuation);
      used += sectionTitleHeight;
    }
  };

  for (let i = 0; i < blocks.length; i += 1) {
    const block = blocks[i];
    const title = isSectionTitle(block);
    const height = outerHeight(block);

    // Um título só "cabe" se o primeiro item da seção couber junto com ele.
    const needed = title
      ? height + (blocks[i + 1] ? outerHeight(blocks[i + 1]) : 0)
      : height;

    // Um bloco maior que a folha inteira não tem como ser quebrado aqui; ele
    // ganha a própria folha e transborda de forma controlada (overflow hidden),
    // em vez de empurrar o resto do documento.
    if (used > 0 && used + needed > usableHeight) {
      breakPage(!title);
    }

    if (title) {
      sectionTitle = block;
      sectionTitleHeight = height;
    }

    current.push(block);
    used += height;
  }

  if (current.length || !pages.length) pages.push(current);

  // A assinatura fecha o documento; se não couber na última folha, ganha uma
  // nova — assinatura órfã cortada ao pé da página invalidaria a receita.
  if (used + signatureHeight > usableHeight) pages.push([]);

  return pages;
}

function buildPageFooter({ prescriptionId, patientName, pageIndex, total }) {
  const left = [
    prescriptionId ? `Prescrição nº ${escapeHtml(prescriptionId)}` : "",
    patientName ? escapeHtml(patientName) : "",
  ]
    .filter(Boolean)
    .join(" · ");

  return `<div class="sv-pdf-pagefoot">
    <span>${left}</span>
    <span>Página ${pageIndex} de ${total}</span>
  </div>`;
}

function createSheet({ letterheadImageUrl, rect, accent }) {
  const sheet = document.createElement("div");
  sheet.className = "sv-pdf-sheet";
  sheet.style.setProperty("--sv-accent", accent);
  if (letterheadImageUrl) {
    sheet.style.backgroundImage = `url("${letterheadImageUrl}")`;
  }

  const area = document.createElement("div");
  area.className = "sv-pdf-area";
  area.style.top = `${rect.top}px`;
  area.style.left = `${rect.left}px`;
  area.style.width = `${rect.width}px`;
  area.style.height = `${rect.height}px`;

  sheet.appendChild(area);
  return { sheet, area };
}

// ────────────────────────────────────────────────────────────────────────────
// API principal
// ────────────────────────────────────────────────────────────────────────────

/**
 * Monta e baixa o PDF do receituário completo.
 *
 * @param {Object} args
 * @param {Object} args.documentData        payload de /api/prescription/{id}/document
 * @param {string} [args.letterheadImageUrl] dataURL do timbre escolhido pelo usuário
 * @param {Object} [args.contentArea]        { top,left,width,height } em % da folha
 * @param {string} args.fileName             nome do arquivo baixado
 * @returns {Promise<number>} quantidade de páginas geradas
 */
export async function downloadPrescriptionPdf({
  documentData,
  letterheadImageUrl = null,
  contentArea = null,
  fileName = "prescricao.pdf",
}) {
  const hasLetterhead = !!letterheadImageUrl;

  const { bodyHtml, signatureHtml, type } = buildPrescriptionDocument(
    documentData,
    { hasLetterhead },
  );

  if (!bodyHtml || !bodyHtml.trim()) {
    throw new Error("Conteúdo vazio");
  }

  ensureStyles();

  const rect = resolveContentRect(hasLetterhead, contentArea);
  const accent = ACCENT[type] || ACCENT.medication;
  const usableHeight = rect.height - PAGE_FOOTER_PX;

  const prescriptionId = documentData?.prescription?.id ?? null;
  const patientName = documentData?.patient?.name ?? "";

  const stage = document.createElement("div");
  stage.className = "sv-pdf-stage";
  document.body.appendChild(stage);

  try {
    // ── 1. Medição ────────────────────────────────────────────────────────
    // Uma folha "de rascunho" com o documento inteiro serve para medir a altura
    // real de cada bloco no mesmo contexto tipográfico da folha final.
    const measurer = createSheet({ letterheadImageUrl, rect, accent });
    measurer.area.style.height = "auto";
    measurer.area.style.overflow = "visible";
    measurer.area.innerHTML =
      `<div class="sv-pdf-body">${bodyHtml}</div>${signatureHtml}`;
    stage.appendChild(measurer.sheet);

    const signatureEl = measurer.area.querySelector(".doc-signature");
    const signatureHeight = signatureEl ? outerHeight(signatureEl) : 0;

    const bodyEl = measurer.area.querySelector(".sv-pdf-body");
    let blocks = collectBlocks(bodyEl);

    // Se o documento vier todo dentro de um único wrapper, sem descer um nível
    // não haveria nada para paginar e o excedente seria cortado.
    while (blocks.length === 1 && blocks[0].children?.length > 1) {
      blocks = collectBlocks(blocks[0]);
    }

    const pages = paginateBlocks(blocks, usableHeight, signatureHeight);

    // Medição concluída: os blocos passam a viver nas folhas finais.
    measurer.sheet.remove();

    // ── 2. Renderização folha a folha ─────────────────────────────────────
    const doc = new jsPDF({
      unit: "mm",
      format: "a4",
      orientation: "portrait",
      compress: true,
    });

    for (let i = 0; i < pages.length; i += 1) {
      const { sheet, area } = createSheet({ letterheadImageUrl, rect, accent });

      // Move os nós já medidos (em vez de re-parsear) para preservar o layout.
      pages[i].forEach((block) => area.appendChild(block));
      if (i === pages.length - 1) {
        area.insertAdjacentHTML("beforeend", signatureHtml);
      }
      area.insertAdjacentHTML(
        "beforeend",
        buildPageFooter({
          prescriptionId,
          patientName,
          pageIndex: i + 1,
          total: pages.length,
        }),
      );

      stage.appendChild(sheet);

      const canvas = await html2canvas(sheet, {
        scale: 2,
        backgroundColor: "#ffffff",
        useCORS: true,
        logging: false,
        width: A4_W_PX,
        height: A4_H_PX,
        windowWidth: A4_W_PX,
        windowHeight: A4_H_PX,
      });

      // Timbre costuma ser imagem rica → JPEG segura o tamanho do arquivo.
      // Documento só de texto fica mais legível em PNG.
      const [dataUrl, format] = hasLetterhead
        ? [canvas.toDataURL("image/jpeg", 0.95), "JPEG"]
        : [canvas.toDataURL("image/png"), "PNG"];

      if (i > 0) doc.addPage("a4", "portrait");
      doc.addImage(dataUrl, format, 0, 0, A4_W_MM, A4_H_MM, undefined, "FAST");

      sheet.remove();
    }

    doc.save(fileName);
    return pages.length;
  } finally {
    stage.remove();
  }
}

export { buildPrescriptionFileName } from "./prescriptionDocument";
